import { ChevronDown } from "lucide-react";
import type React from "react";
import { useState } from "react";

interface FAQItem {
	id: string;
	question: string;
	answer: React.ReactNode;
}

const faqItems: FAQItem[] = [
	{
		id: "fees",
		question: "What fees are charged?",
		answer: (
			<div className="space-y-4">
				<p className="text-gray-600">
					Two sets of fees apply to each payment: a payment processing fee and a Sehara platform
					fee.
				</p>

				<div className="overflow-x-auto">
					<table className="w-full text-sm border-collapse">
						<thead>
							<tr className="bg-gray-50">
								<th className="text-left px-4 py-2 font-semibold text-gray-700 border border-gray-200">
									Payment method
								</th>
								<th className="text-left px-4 py-2 font-semibold text-gray-700 border border-gray-200">
									Processing fee
								</th>
								<th className="text-left px-4 py-2 font-semibold text-gray-700 border border-gray-200">
									Sehara fee
								</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td className="px-4 py-2 text-gray-700 border border-gray-200">Card</td>
								<td className="px-4 py-2 text-gray-700 border border-gray-200">1.9% + 20p</td>
								<td className="px-4 py-2 text-gray-700 border border-gray-200 whitespace-nowrap">
									1.5%
								</td>
							</tr>
							<tr className="bg-gray-50">
								<td className="px-4 py-2 text-gray-700 border border-gray-200">
									Buy now, pay later (Klarna)
								</td>
								<td className="px-4 py-2 text-gray-700 border border-gray-200">4.99% + 35p</td>
								<td className="px-4 py-2 text-gray-700 border border-gray-200 whitespace-nowrap">
									1.5%
								</td>
							</tr>
							<tr>
								<td className="px-4 py-2 text-gray-700 border border-gray-200">
									Buy now, pay later (Clearpay)
								</td>
								<td className="px-4 py-2 text-gray-700 border border-gray-200">6% + 30p</td>
								<td className="px-4 py-2 text-gray-700 border border-gray-200 whitespace-nowrap">
									1.5%
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<div className="bg-orange-50 border border-orange-100 rounded-lg p-4">
					<p className="text-sm font-semibold text-gray-800 mb-2">
						Example: £200 session paid via Klarna
					</p>
					<ul className="text-sm text-gray-700 space-y-1">
						<li className="flex justify-between">
							<span>Session amount</span>
							<span className="font-medium">£200.00</span>
						</li>
						<li className="flex justify-between">
							<span>Klarna fee (4.99% + 35p)</span>
							<span className="font-medium text-red-600">− £10.33</span>
						</li>
						<li className="flex justify-between">
							<span>Sehara fee (1.5%)</span>
							<span className="font-medium text-red-600">− £3.00</span>
						</li>
						<li className="flex justify-between border-t border-orange-200 pt-1 mt-1">
							<span className="font-semibold">You receive</span>
							<span className="font-semibold text-green-700">£186.67</span>
						</li>
					</ul>
				</div>
			</div>
		),
	},
	{
		id: "payment-methods",
		question: "What payment methods can my customers use?",
		answer: (
			<div className="space-y-3">
				<p className="text-gray-600">
					Customers can choose from a range of payment options at checkout:
				</p>
				<ul className="text-sm text-gray-700 space-y-2">
					<li className="flex items-start gap-2">
						<span className="mt-0.5 text-orange-500 font-bold">•</span>
						<span>
							<span className="font-semibold">Card</span> — all major debit and credit cards
							accepted
						</span>
					</li>
					<li className="flex items-start gap-2">
						<span className="mt-0.5 text-orange-500 font-bold">•</span>
						<span>
							<span className="font-semibold">Klarna</span> — pay in 3 interest-free instalments (0%
							interest available)
						</span>
					</li>
					<li className="flex items-start gap-2">
						<span className="mt-0.5 text-orange-500 font-bold">•</span>
						<span>
							<span className="font-semibold">Clearpay</span> — split into 4 interest-free payments
						</span>
					</li>
					<li className="flex items-start gap-2">
						<span className="mt-0.5 text-orange-500 font-bold">•</span>
						<span>
							<span className="font-semibold">Apple Pay & Google Pay</span> — available where
							supported by the customer's device
						</span>
					</li>
				</ul>
			</div>
		),
	},
	{
		id: "how-links-work",
		question: "How do payment links work?",
		answer: (
			<div className="space-y-3">
				<p className="text-gray-600">Creating and sharing a payment link takes just a few steps:</p>
				<ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
					<li>
						Create a payment link from your dashboard with the session amount and description.
					</li>
					<li>Share the link with your customer via DM, or let them scan the QR code.</li>
					<li>Your customer completes payment online — you get notified instantly.</li>
				</ol>
				<p className="text-sm text-gray-500">
					Payment links expire after <span className="font-semibold">7 days</span> if unused.
				</p>
			</div>
		),
	},
	{
		id: "when-paid",
		question: "When do I get paid?",
		answer: (
			<div className="space-y-4">
				<p className="text-gray-600">
					When a customer pays, the money is captured straight away — but you receive funds when
					your payment provider sends a <span className="font-medium">payout</span> to your
					connected bank account. For UK accounts, the standard{" "}
					<span className="font-medium">T+3 settlement timing</span> applies: your balance is
					available for payout within three business days of the payment being captured.
				</p>

				<p className="text-gray-600">
					<span className="font-medium">Business days</span> are working days (Monday to Friday),
					excluding weekends and public holidays. Payouts scheduled on a weekend or bank holiday
					arrive on the next business day.
				</p>

				<div className="bg-orange-50 border border-orange-100 rounded-lg p-4">
					<p className="text-sm font-semibold text-gray-800 mb-2">
						Example: £150 session paid on a Monday
					</p>
					<ul className="text-sm text-gray-700 space-y-1">
						<li className="flex justify-between">
							<span>Customer pays (Monday)</span>
							<span className="font-medium">Day 0</span>
						</li>
						<li className="flex justify-between">
							<span>Funds become available (T+3 business days)</span>
							<span className="font-medium">Thursday</span>
						</li>
						<li className="flex justify-between border-t border-orange-200 pt-1 mt-1">
							<span className="font-semibold">Payout sent to your bank</span>
							<span className="font-semibold text-green-700">Thursday</span>
						</li>
					</ul>
					<p className="text-xs text-gray-500 mt-2">
						If the customer paid on a Friday, the three business days would run Monday–Wednesday, so
						the payout would typically arrive on Wednesday.
					</p>
				</div>

				<p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-4 py-3">
					<span className="font-semibold">First payout:</span> When you start processing live
					payments, your payment provider typically schedules your initial payout 7–14 days after
					your first successful payment while it completes fraud and identity checks. Subsequent
					payouts follow the T+3 business-day schedule above.
				</p>

				<p className="text-sm text-gray-500">
					In Sehara, open the Account page to view your balance and payout history.
				</p>
			</div>
		),
	},
	{
		id: "setup-requirements",
		question: "What do I need to set up before creating payment links?",
		answer: (
			<div className="space-y-3">
				<p className="text-gray-600">
					Two things must be completed before you can create payment links:
				</p>
				<ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
					<li>
						<span className="font-semibold">Complete onboarding</span> — verify your identity and
						connect your bank account via the Account page. This is handled securely by our payment
						provider.
					</li>
					<li>
						<span className="font-semibold">Upload your Terms & Conditions</span> — go to Settings
						and upload your T&C document. Customers will be required to accept it before paying.
					</li>
				</ol>
			</div>
		),
	},
	{
		id: "minimum-amount",
		question: "Is there a minimum payment amount?",
		answer: (
			<p className="text-gray-600">
				Yes. The minimum payment amount is <span className="font-semibold">£1.00</span>.
			</p>
		),
	},
	{
		id: "refunds",
		question: "How do refunds work?",
		answer: (
			<p className="text-gray-600">
				Refunds can be processed directly from the Account area of Sehara. You will need to
				re-authenticate your credentials when you get there. Once authenticated, you can view your
				transactions and select any payment record shown to issue a refund against it.
			</p>
		),
	},
	{
		id: "paypal",
		question: "Do you support PayPal?",
		answer: (
			<p className="text-gray-600">
				PayPal support is coming soon. Currently, payments can be made by card, Klarna, or Clearpay.
				We'll notify you when PayPal becomes available.
			</p>
		),
	},
];

const FAQAccordionItem: React.FC<{ item: FAQItem }> = ({ item }) => {
	const [isOpen, setIsOpen] = useState(false);

	return (
		<div className="border border-gray-200 rounded-lg overflow-hidden">
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				className="w-full flex items-center justify-between px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors"
			>
				<span className="font-semibold text-gray-900">{item.question}</span>
				<ChevronDown
					className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
				/>
			</button>
			{isOpen && <div className="px-6 py-4 bg-white border-t border-gray-100">{item.answer}</div>}
		</div>
	);
};

export default function FAQ() {
	return (
		<div className="min-h-screen w-full bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50">
			<main className="p-4 sm:p-8">
				<div className="max-w-3xl mx-auto">
					<div className="mb-8">
						<h1 className="text-2xl font-semibold text-gray-900">Frequently Asked Questions</h1>
						<p className="mt-2 text-gray-500">Everything you need to know about using Sehara.</p>
					</div>

					<div className="space-y-3">
						{faqItems.map((item) => (
							<FAQAccordionItem key={item.id} item={item} />
						))}
					</div>
				</div>
			</main>
		</div>
	);
}
